import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"

import tokens from "../_tokens"
import {
  Head,
  Header,
  PageEditor,
  PageFooter,
  Sidebar,
  TableOfContents,
} from "./"

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  z-index: 0;
`

const StyledMain = styled.main`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledGrid = styled.div`
  padding: ${4 * tokens.size.base}px ${5 * tokens.size.base}px;
  padding-right: ${4 * tokens.size.base}px;
  max-width: 100%;
  display: grid;
  grid-template-columns: 100%;
  grid-template-areas:
    "heading"
    "content";
  grid-row-gap: ${1.5 * tokens.size.base}px;
  row-gap: ${1.5 * tokens.size.base}px;
  align-items: start;
  align-self: start;
  grid-row-gap: 0;

  @media screen and (min-width: ${tokens.breakpoint.l}px) {
    grid-template-columns: minmax(0, 100ch) ${tokens.size
        .tableOfContentsWidth}px;
    grid-template-areas:
      "heading ."
      "content table-of-contents";
    grid-column-gap: ${7 * tokens.size.base}px;
    padding: ${6 * tokens.size.base}px ${7 * tokens.size.base}px;
  }

  @media screen and (min-width: ${tokens.breakpoint.xl}px) {
    grid-column-gap: ${7 * tokens.size.base}px;
    padding-right: ${4 * tokens.size.base}px;
  }
`
const StyledTOCContainer = styled.div`
  display: none;
  max-height: calc(100vh - ${tokens.size.headerHeight}px - 24px);
  position: sticky;
  top: ${tokens.size.headerHeight + 24}px;
  grid-area: table-of-contents;
  overflow: auto;
  @media screen and (min-width: ${tokens.breakpoint.l}px) {
    display: block;
  }
`

const StyledTOCCollapsibleContainer = styled.div`
  margin-bottom: ${1.5 * tokens.size.base}px;
  display: block;
  @media screen and (min-width: ${tokens.breakpoint.l}px) {
    display: none;
  }
`

const StyledMarkdownContentContainer = styled.div`
  grid-area: content;
`

export const LayoutBase = ({ children, pageContext, headerComponent }) => {
  const {
    title,
    description,
    additionalContributors = [],
  } = pageContext.frontmatter

  const { slug, rawBody, fileRelativePath } = pageContext

  return (
    <StyledContainer id="skip-nav">
      <Head title={title} description={description} />
      <Header />
      <StyledContentContainer>
        <Sidebar />
        <StyledMain>
          {headerComponent}
          <StyledGrid id="skip-nav">
            {pageContext.tableOfContents.items && (
              <StyledTOCContainer>
                <TableOfContents items={pageContext.tableOfContents.items} />
              </StyledTOCContainer>
            )}
            <StyledMarkdownContentContainer>
              {pageContext.tableOfContents.items && (
                <StyledTOCCollapsibleContainer>
                  <TableOfContents
                    isCollapsible
                    items={pageContext.tableOfContents.items}
                  />
                </StyledTOCCollapsibleContainer>
              )}
              {__EDIT__ ? (
                <PageEditor
                  slug={slug}
                  fileRelativePath={fileRelativePath}
                  rawBody={rawBody}
                />
              ) : (
                children
              )}
              <PageFooter
                editUrl={pageContext.editUrl}
                contributors={pageContext.contributors.concat(
                  additionalContributors.map(login => ({ login }))
                )}
              />
            </StyledMarkdownContentContainer>
          </StyledGrid>
        </StyledMain>
      </StyledContentContainer>
    </StyledContainer>
  )
}

LayoutBase.propTypes = {
  children: PropTypes.node,
  pageContext: PropTypes.object,
  headerComponent: PropTypes.element,
}

const StyledHeading = styled.h1`
  border: 0;
  margin: 0;
  padding: ${5 * tokens.size.base}px ${7 * tokens.size.base}px;
  padding-bottom: 0;
  font-size: ${tokens.fontSize.heading.xl};
  grid-area: heading;
`
const LayoutHeading = ({ title }) => <StyledHeading>{title}</StyledHeading>

LayoutHeading.propTypes = {
  title: PropTypes.string,
}

function Layout({
  pageContext: {
    frontmatter: { title },
  },
  pageContext,
  ...props
}) {
  return (
    <LayoutBase
      headerComponent={<LayoutHeading title={title} />}
      pageContext={pageContext}
      {...props}
    />
  )
}

Layout.propTypes = {
  pageContext: PropTypes.object,
}
export default Layout
